<template>
  <ais-instant-search :search-client="searchClient"
                      :routing="routing"
                      :insights="true"
                      :future="{
                        preserveSharedStateOnUnmount: true
                      }"
                      :index-name="algoliaIndexName"
                      :middlewares="middlewares">
    <ais-configure :hitsPerPage="40"/>
    <ais-configure v-if="hasFilters" :filters="filters"/>

    <div class="filter-search-container d-flex align-items-center px-2">
      <ais-search-box :placeholder="`Search for ${assetTypePrefix}`" class="w-100">
        <div slot="submit-icon">
          <b-img :src="filterSearchIconURL"></b-img>
        </div>
        <div slot="reset-icon">
          <b-img :src="filterCancelIconURL"></b-img>
        </div>
      </ais-search-box>
    </div>

    <div class="tw-mt-4 tw-flex tw-gap-4">
      <filter-column class="tw-w-fit tw-hidden lg:tw-block"
                     :filterCancelIconUrl="filterCancelIconURL"
                     :filterIconUrl="filterIconURL"/>

      <div class="tw-flex-1 tw-flex tw-flex-col tw-gap-4 tw-pb-4">
        <search-banner v-if="!isLoggedIn"/>

        <div class="d-flex align-items-lg-center flex-column flex-lg-row justify-content-between">
          <div class="mb-2 mb-lg-0">
            <ais-stats #default="{ hitsPerPage, nbPages, nbHits, page, processingTimeMS, query }">
              <p class="font-weight-semi-bold m-0">
                <span v-if="nbHits === 0">No results matching the search criteria</span>
                <span v-else>
                  Displaying <span class="text-primary">{{ nbHits }}</span> results matching your keywords.
                </span>
              </p>
            </ais-stats>
          </div>

          <div class="tw-flex tw-items-center tw-gap-4">
            <ais-pagination
              :class-names="{'ais-Pagination-list': 'pagination','ais-Pagination-item': 'page-item','ais-Pagination-link': 'page-link', 'ais-Pagination-item--selected': 'active', 'ais-Pagination-item--disabled': 'disabled'}"/>

            <grid-display-switch v-model="gridDisplayType"/>
          </div>

        </div>

        <div class="search-results-container p-3">
          <ais-hits #default="{ items }">

            <!-- the v-show here is a hack. when switching from masonry to grid something messes with the indexes if we do v-if -->
            <div :class="{ '!tw-hidden': gridDisplayType !== 'masonry'}">
              <MasonryWall :items="items"
                           :gap="20"
                           :max-columns="3">
                <template #default="{ item: asset, index }">
                  <masonry-asset-card :asset="asset"
                                      :user-slug="userSlugs[asset.user_uuid]"
                                      @preview="showModalAsset = asset"
                                      :projectsUrl="projectsUrl"
                                      :projectAssetsUrl="projectAssetsUrl"/>
                </template>
              </MasonryWall>
            </div>


            <div v-if="gridDisplayType === 'grid'" class="tw-grid tw-grid-cols-3 tw-gap-4">
              <grid-asset-card v-for="asset in items"
                               :key="asset.objectID"
                               :asset="asset"
                               :user-slug="userSlugs[asset.user_uuid]"
                               @preview="showModalAsset = asset"
                               :projectsUrl="projectsUrl"
                               :projectAssetsUrl="projectAssetsUrl"/>
            </div>

          </ais-hits>
        </div>

        <ais-pagination
          class="tw-self-end"
          :class-names="{'ais-Pagination-list': 'pagination','ais-Pagination-item': 'page-item','ais-Pagination-link': 'page-link', 'ais-Pagination-item--selected': 'active', 'ais-Pagination-item--disabled': 'disabled'}"/>
      </div>
    </div>

    <ais-state-results #default="{ results: { hits } }">
      <ContributorSlugFetcher :items="hits" @userSlugs="userSlugs = $event"/>
    </ais-state-results>

    <poc-modal-asset v-model="showModalAsset"/>
  </ais-instant-search>
</template>

<script>
import {history as historyRouter} from 'instantsearch.js/es/lib/routers';
import algoliasearch from 'algoliasearch/lite';
import AssetLoader from '../../../AssetLoader';
import AddToProject from '../Customers/Dashboard/AddToProject';
import GeneralMixins from "../../mixins/GeneralMixins";
import PocModalAsset from "../Common/PocModalAsset.vue";
import VideoHoverPlayer from "../Common/VideoHoverPlayer.vue";
import SearchBanner from "./SearchBanner.vue";
import MasonryAssetView from "../Search/MasonryAssetView.vue";
import FilterColumn from "./FilterColumn.vue";
import GridAssetCard from "./GridAssetCard.vue";
import MasonryAssetCard from "./MasonryAssetCard.vue";
import GridDisplaySwitch from "./GridDisplaySwitch.vue";
import ContributorSlugFetcher from "./ContributorSlugFetcher.vue";
import MasonryWall from "../Common/MasonryWall.vue";

function getTypeSlug(name) {
  return `${name.toLowerCase()}s`
}

function getTypeName(slug) {
  const lastChar = slug.slice(-1);
  if (lastChar === 's') {
    slug = slug.slice(0, -1);
  }

  return `${slug.capitalized()}`
}

export default {

  props: ['algoliaAppId', 'algoliaSearchKey', 'algoliaIndexName', 'projectsUrl', 'projectAssetsUrl', 'userId', 'submissionId', 'assetType'],

  mixins: [GeneralMixins],

  data() {
    var self = this;

    return {
      gridDisplayType: 'masonry',
      showModalAsset: null,
      middlewares: [this.middleware],
      currentType: null,
      userSlugs: {},
      searchClient: algoliasearch(
        this.algoliaAppId,
        this.algoliaSearchKey
      ),
      routing: {
        router: historyRouter({
          windowTitle({type, query}) {
            // Hack to get the current asset type. It's not populated at initial runtime
            self.currentType = type

            const queryTitle = query ? `Results for "${query}"` : 'Search';

            if (type) {
              return `${type} – ${queryTitle}`;
            }

            return queryTitle;
          },

          cleanUrlOnDispose: true,

          createURL({qsModule, routeState, location}) {
            const urlParts = location.href.match(/^(.*?)\/search/);
            const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

            const typePath = routeState.type
              ? `${getTypeSlug(routeState.type)}/`
              : '';

            const queryParameters = {};

            if (routeState.query) {
              queryParameters.query = encodeURIComponent(routeState.query);
            }
            if (routeState.page !== 1) {
              queryParameters.page = routeState.page;
            }

            const filters = ['categories', 'premium', 'race', 'ethnicity', 'gender', 'usage', 'number_of_people', 'ages']

            filters.forEach(f => {
              if (routeState[f]) {
                queryParameters[f] = routeState[f].map(encodeURIComponent);
              }
            })

            const queryString = qsModule.stringify(queryParameters, {
              addQueryPrefix: true,
              arrayFormat: 'repeat',
            });

            const url = `${baseUrl}search/${typePath}${queryString}`;
            return url
          },

          parseURL({qsModule, location}) {
            const pathnameMatches = location.pathname.match(/search\/(.*?)\/?$/);
            let typePathName = getTypeName((pathnameMatches && pathnameMatches[1]) || '');
            let {
              query = '',
              page,
              categories = [],
              race = [],
              ethnicity = [],
              gender = [],
              premium = null,
              usage = [],
              ages = [],
              number_of_people = [],
              type = null
            } = qsModule.parse(location.search.slice(1));

            // `qs` does not return an array when there's a single value.
            const allCategories = Array.isArray(categories) ? categories : [categories].filter(Boolean);
            const allRaces = Array.isArray(race) ? race : [race].filter(Boolean);
            const allEth = Array.isArray(ethnicity) ? ethnicity : [ethnicity].filter(Boolean);
            const allGender = Array.isArray(gender) ? gender : [gender].filter(Boolean);
            const allUsage = Array.isArray(usage) ? usage : [usage].filter(Boolean);
            const allNumberOfPeople = Array.isArray(number_of_people) ? number_of_people : [number_of_people].filter(Boolean);
            const allAges = Array.isArray(ages) ? ages : [ages].filter(Boolean);

            typePathName = typePathName || type

            // Hack to get the current asset type. seems to only be run once - used for initial populating
            if (typePathName) {
              self.currentType = typePathName
            }

            const result = {
              query: decodeURIComponent(query),
              page,
              categories: allCategories.map(decodeURIComponent),
              race: allRaces.map(decodeURIComponent),
              ethnicity: allEth.map(decodeURIComponent),
              gender: allGender.map(decodeURIComponent),
              usage: allUsage.map(decodeURIComponent),
              number_of_people: allNumberOfPeople.map(decodeURIComponent),
              ages: allAges.map(decodeURIComponent),
              type: typePathName,
            }

            if (premium !== null) {
              result.premium = [premium]
            }

            return result;
          },
        }),

        stateMapping: {
          indexName: this.algoliaIndexName,

          stateToRoute(uiState) {
            const indexUiState = uiState[this.indexName] || {};

            const result = {
              query: indexUiState.query,
              page: indexUiState.page,
              categories: indexUiState.refinementList && indexUiState.refinementList.categories,
              race: indexUiState.refinementList && indexUiState.refinementList.race,
              ethnicity: indexUiState.refinementList && indexUiState.refinementList.ethnicity,
              gender: indexUiState.refinementList && indexUiState.refinementList.gender,
              number_of_people: indexUiState.refinementList && indexUiState.refinementList.number_of_people,
              ages: indexUiState.refinementList && indexUiState.refinementList.ages,
              premium: indexUiState.refinementList && indexUiState.refinementList.premium,
              type: indexUiState.menu && indexUiState.menu.type,
              // usage: indexUiState.menu && indexUiState.menu.usage,
              usage: indexUiState.refinementList && indexUiState.refinementList.usage,
            }

            return result;
          },

          routeToState(routeState) {
            const result = {
              [this.indexName]: {
                query: routeState.query,
                page: routeState.page,
                menu: {
                  // usage: routeState.usage,
                  type: routeState.type
                },
                refinementList: {
                  usage: routeState.usage,
                  categories: routeState.categories,
                  race: routeState.race,
                  ethnicity: routeState.ethnicity,
                  gender: routeState.gender,
                  number_of_people: routeState.number_of_people,
                  ages: routeState.ages,
                  premium: routeState.premium
                },
              },
            }

            return result
          },
        },
      }
    };
  },

  mounted() {
    if (window.aa) {
      window.aa('setAuthenticatedUserToken', window.POC.uid)
    }
  },

  methods: {

    middleware({instantSearchInstance}) {
      const vm = this
      return {
        onStateChange({uiState}) {
          if (uiState && Object.keys(uiState)) {
            const query = uiState[Object.keys(uiState)[0]].query
            vm.search(query, vm)
          }
        },
        subscribe() {
        },
        unsubscribe() {
        },
      }
    },

    search: _.debounce((query, vm) => {
      if (query && query.length) {
        vm.$http.post("/customers/search_histories", {query: query})
      }
    }, 4000),
  },

  computed: {
    isLoggedIn() {
      return window.POC.uid
    },

    assetTypePrefix() {
      if (this.currentType === "Video") {
        return "videos"
      } else if (this.currentType === "Photo") {
        return "photos"
      } else if (this.currentType === "Illustration") {
        return "illustrations"
      } else {
        return "assets"
      }
    },

    filters() {
      if (this.userId) {
        return `user_uuid:${this.userId}`
      }

      if (this.submissionId) {
        return `submission_uuid:${this.submissionId}`
      }
    },

    hasFilters() {
      return this.userId || this.submissionId
    },

    filterIconURL() {
      return AssetLoader.imagePath('pages/ic-filter.png');
    },

    filterCancelIconURL() {
      return AssetLoader.imagePath('pages/ic-cancel.png');
    },

    filterSearchIconURL() {
      return AssetLoader.imagePath('pages/ic-search.png');
    },

    subscriptionBannerURL() {
      return AssetLoader.imagePath('subscription_ad_1.png');
    }
  },

  components: {
    MasonryWall,
    ContributorSlugFetcher,
    GridDisplaySwitch,
    MasonryAssetCard,
    GridAssetCard,
    FilterColumn,
    MasonryAssetView,
    SearchBanner,
    VideoHoverPlayer,
    PocModalAsset,
    AddToProject
  }
};
</script>
